import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable } from '@angular/core';
import { env, InferenceSession, Tensor } from 'onnxruntime-web';
import { BehaviorSubject, forkJoin } from 'rxjs';
import { ImageTextboxExtractorService } from './image-textbox-extractor.service';
import { ImageRaw, LineImage, ReshapeOptions, ScanResult, Size } from './licence-plate-recognition.model';

export type Line = {
  text: string;
  mean: number;
  box?: number[][];
}


const BASE_SIZE = 32

@Injectable({
  providedIn: 'root'
})
export class LicencePlateRecognitionService {

  private detectionInferenceSession: InferenceSession;
  private recognitionInferenceSession: InferenceSession;
  private recognitionDictionary: Array<string>;

  public initialized = new BehaviorSubject(false);

  constructor(private httpClient: HttpClient, private imageTextBoxExtractionService: ImageTextboxExtractorService, private destroyRef: DestroyRef) {
	this.init();
  }

  public async detectLicencePlate(imageData: ImageData): Promise<ScanResult[]> {
    const lineImages = await this.findLicencePlateInImage(imageData);
    if (lineImages == null || lineImages.length === 0) {
      return [];
    }
    const recognized = await this.recognizeLicencePlateFromLineImages(lineImages);
    return recognized.map(r => new ScanResult({ text: r.text, confidence: r.mean, location: r.box }) ).sort((a, b) => b.confidence - a.confidence);
  }


  private async recognizeLicencePlateFromLineImages(lineImages: LineImage[]) {
    const modelDatas = await Promise.all(
      // Detect text from each line image
      lineImages.map(async (lineImage, index) => {
        // Resize Image to 48px height
        //  - height must <= 48
        //  - height: 48 is more accurate then 40, but same as 30
        const image = await lineImage.image.resize({
          height: 48,
        });

        // transform image data to model data
        const modelData = this.imageToModel(image, {});
        return modelData;
      }),
    );
    const allLines: Line[] = [];

    for (const modelData of modelDatas) {
      // Run model for each line image
      const input = Float32Array.from(modelData.data);
      const inputTensor = new Tensor('float32', input, [1, 3, modelData.height, modelData.width]);
      const feeds = { [this.recognitionInferenceSession.inputNames[0]]: inputTensor };
      const outputs = await this.recognitionInferenceSession.run(feeds);
      const output = outputs[this.recognitionInferenceSession.outputNames[0]];
      // use Dictoinary to decode output to text
      const lines = await this.decodeText(output);
      allLines.unshift(...lines);
    }
    return allLines;
  }


  /**
   * canvas.getContext('2d')!.getImageData(0, 0, canvas.width, canvas.height)
   * @param ImageData https://developer.mozilla.org/de/docs/Web/API/ImageData
   */
  private async findLicencePlateInImage(imageData: ImageData) {

    const imageRaw = new ImageRaw({data: imageData.data, width: imageData.width, height: imageData.height});

    const inputImage = await imageRaw.resize(this.multipleOfBaseSize(imageRaw));

    const modelData = this.imageToModel(inputImage, {});
    const input = Float32Array.from(modelData.data);
    const inputTensor = new Tensor('float32', input, [1, 3, modelData.height, modelData.width]);

    const feeds = { [this.detectionInferenceSession.inputNames[0]]: inputTensor };
    const results = await this.detectionInferenceSession.run(feeds);
    const result = results[this.detectionInferenceSession.outputNames[0]];
    const outputImage = this.tensorToMat(result, 0.03);

    const lineImages = await this.imageTextBoxExtractionService.extractTextboxes(outputImage, inputImage);

    return lineImages;
  }

  private tensorToMat(output: Tensor, threshold: number) {
    const height = output.dims[2];
    const width = output.dims[3];
    const data = new Uint8Array(width * height * 4);
    for (const [outIndex, outValue] of output.data.entries()) {
      const n = outIndex * 4;
      const value = (outValue as number) > threshold ? 255 : 0;
      data[n] = value; // R
      data[n + 1] = value; // G
      data[n + 2] = value; // B
      data[n + 3] = 255; // A
    }
    return new ImageRaw({ data, width, height });
  }

  private imageToModel(image: ImageRaw, { mean = [0, 0, 0], std = [1, 1, 1] }: ReshapeOptions) {
    const R: number[] = [];
    const G: number[] = [];
    const B: number[] = [];
    for (let i = 0; i < image.data.length; i += 4) {
      R.push((image.data[i] / 255 - mean[0]) / std[0]);
      G.push((image.data[i + 1] / 255 - mean[1]) / std[1]);
      B.push((image.data[i + 2] / 255 - mean[2]) / std[2]);
    }
    const newData = Float32Array.from([...B, ...G, ...R]);
    return {
      data: newData,
      width: image.width,
      height: image.height,
    }

  }

  private decodeText(output: Tensor) {
    const data = output;
    const predLen = data.dims[2];
    const line: Line[] = [];
    let ml = data.dims[0] - 1;
    for (let l = 0; l < data.data.length; l += predLen * data.dims[1]) {
      const predsIdx: number[] = [];
      const predsProb: number[] = [];

      for (let i = l; i < l + predLen * data.dims[1]; i += predLen) {
        const tmpArr = data.data.slice(i, i + predLen) as Float32Array;
        const tmpMax = tmpArr.reduce((a, b) => Math.max(a, b), Number.NEGATIVE_INFINITY);
        const tmpIdx = tmpArr.indexOf(tmpMax);
        predsProb.push(tmpMax);
        predsIdx.push(tmpIdx);
      }
      line[ml] = this.decode(this.recognitionDictionary, predsIdx, predsProb, true);
      ml--;
    }
    return line;
  }

  private decode(dictionary: string[], textIndex: number[], textProb: number[], isRemoveDuplicate: boolean) {
    const ignoredTokens = [0];
    const charList = [];
    const confList = [];
    for (let idx = 0; idx < textIndex.length; idx++) {
      if (textIndex[idx] in ignoredTokens) {
        continue;
      }
      if (isRemoveDuplicate) {
        if (idx > 0 && textIndex[idx - 1] === textIndex[idx]) {
          continue;
        }
      }
      charList.push(dictionary[textIndex[idx] - 1]);
      if (textProb) {
        confList.push(textProb[idx]);
      } else {
        confList.push(1);
      }
    }
    let text = '';
    let mean = 0;
    if (charList.length) {
      text = charList.join('');
      let sum = 0;
      confList.forEach((item) => {
        sum += item;
      })
      mean = sum / confList.length;
    }
    return { text, mean };
  }

  private multipleOfBaseSize(image: ImageRaw, { maxSize }: { maxSize?: number } = {}): Size {
    let width = image.width;
    let height = image.height;
    if (maxSize && Math.max(width, height) > maxSize) {
      const ratio = width > height ? maxSize / width : maxSize / height;
      width = width * ratio;
      height = height * ratio;
    }
    const newWidth = Math.max(
      Math.ceil(width / BASE_SIZE) * BASE_SIZE,
      BASE_SIZE,
    );
    const newHeight = Math.max(Math.ceil(height / BASE_SIZE) * BASE_SIZE, BASE_SIZE);
    return { width: newWidth, height: newHeight };
  }


  public async init() {
    const start = new Date().getTime();
    console.log('initialize start');
    env.wasm.wasmPaths = {
      mjs: '/assets/default/ocr/ort-wasm-simd-threaded.js',
      wasm: '/assets/default/ocr/ort-wasm-simd-threaded.wasm'
    };

    env.webgpu.powerPreference = 'high-performance';

    const result = await forkJoin({
      detectionModel: this.httpClient.get('/assets/default/ocr/detection.onnx', { responseType: 'arraybuffer' }),
      recognitionModel: this.httpClient.get('/assets/default/ocr/recognition.onnx', { responseType: 'arraybuffer' }),
      dictionaryModel: this.httpClient.get('/assets/default/ocr/dictionary.txt', { responseType: 'text' })
    }).toPromise();

    const detectionModelUrl = URL.createObjectURL(new Blob([result.detectionModel!], { type: 'application/octet-stream' }));
    const recognitionModelUrl = URL.createObjectURL(new Blob([result.recognitionModel!], { type: 'application/octet-stream' }));


    let executionProviders = ['wasm'];
    // if ('gpu' in navigator) {
    //   executionProviders = ['webgpu'];
    // }

    console.log('initialize detection session', new Date().getTime() - start, 'ms');
    this.detectionInferenceSession = await InferenceSession.create(detectionModelUrl, {
      executionProviders: executionProviders
      });
    console.log('initialize detection session finished:', new Date().getTime() - start, 'ms');
    this.recognitionInferenceSession = await InferenceSession.create(recognitionModelUrl, {
      executionProviders: executionProviders
      });
      console.log('initialize recognition session finished:', new Date().getTime() - start, 'ms');
    this.recognitionDictionary = [...result.dictionaryModel.split('\n'), ' '];
    console.log('initialize done in', new Date().getTime() - start, 'ms');
    this.initialized.next(true);

    this.destroyRef.onDestroy(() => {
      console.log('LicencePlateRecognitionService destroyed');
      URL.revokeObjectURL(detectionModelUrl);
      URL.revokeObjectURL(recognitionModelUrl);
    });
  }
}
